/**
 *
 * TabStudentCourses
 *
 */

import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { Link } from 'react-router-dom';

// styles
import './style.scss';

// antd component
import { Form, Select } from 'antd';

// assets
import { BookOutlined } from '@ant-design/icons';

// components
import CustomTable from 'components/CustomTable';
import AddItemCard from 'components/AddItemCard';

// helper
import { getUniqueArrayObj } from 'components/Tabs/helper.js';
// utils
import { getLevelLabel } from 'utils/helpers/helper.generateData';

function TabStudentCourses({
  courses,
  classInfo,
  disabeled,
  // actions
  updateList,
  onFormChange,
  // default
  className,
  ...rest
}) {
  const childClassNames = classNames(
    'tab-students-courses-wrapper row-tabs',
    className,
  );
  // student courses list
  const [currentCourses, setCurrentCourses] = useState(courses || []);
  // table rows
  const [currentClassCourses, setCurrentClassCourses] = useState(
    classInfo?.courses || [],
  );
  const [tableRows, setTableRow] = useState([]);
  const [formHaveChanges, setFormHaveChanges] = useState(false);

  // Form initialization for new row
  const [coursesSelectedForm] = Form.useForm();

  // Set data for opened item
  useEffect(() => {
    setFormHaveChanges(false);
    courses && setCurrentCourses(courses);
    classInfo?.courses && setCurrentClassCourses(classInfo?.courses);
    if (classInfo && classInfo?.courses) {
      const rows = classInfo?.courses.map(elem => {
        return {
          ...elem,
          name: elem?.subject?.name,
          color: elem?.subject?.color,
        };
      });
      const uniqueRows = rows && getUniqueArrayObj(rows, 'name');
      setTableRow(uniqueRows);
    }
  }, []);

  // Set data for opened item
  useEffect(() => {
    setFormHaveChanges(false);
    if (courses) {
      setCurrentCourses(courses);
      // set current student courses
      const initCourse = {};
      courses.map(course => {
        initCourse[(course?.subject?.name)] = course?._id;
      });
      coursesSelectedForm.setFieldsValue(initCourse);
    }
    // set courses list
    if (
      classInfo &&
      classInfo?.courses &&
      JSON.stringify(currentClassCourses) !== JSON.stringify(classInfo?.courses)
    ) {
      classInfo?.courses && setCurrentClassCourses(classInfo?.courses);
      const rows = classInfo?.courses.map(elem => {
        return {
          ...elem,
          name: elem?.subject?.name,
          color: elem?.subject?.color,
        };
      });
      const uniqueRows = rows && getUniqueArrayObj(rows, 'name');
      setTableRow(uniqueRows);
    }
  }, [classInfo?.courses, courses, currentClassCourses]);

  // Update if form have any changes for parent component
  useEffect(() => {
    onFormChange(formHaveChanges);
  }, [formHaveChanges]);

  // Save courses list and update
  const saveCoursesList = () => {
    coursesSelectedForm.submit();
    const values = coursesSelectedForm.getFieldsValue();
    if (values && JSON.stringify(values).length > 0) {
      let newEls = [];
      // get only values from form object
      for (const key in values) {
        values[key] && newEls.push(values[key]);
      }
      updateList(newEls);
      setFormHaveChanges(false);
    }
  };

  const getCourseBySubject = subjectName => {
    return currentClassCourses.filter(
      course => course?.subject?.name == subjectName && course,
    );
  };

  // Courses select by level form
  const coursesLevels = record => {
    const selectName = `${record?.name}`;
    const coursesByName = getCourseBySubject(record?.name);
    const options = coursesByName.map(course => ({
      key: course._id,
      label: getLevelLabel(course?.level),
      value: course._id,
      color: course.subject.color,
      item: course,
    }));
    return (
      <Form.Item name={selectName}>
        <Select
          className="table-select"
          placeholder="-"
          options={options}
          onSelect={(LabeledValue, option) => {
            setFormHaveChanges(true);
          }}
          getPopupContainer={trigger => trigger.parentElement}
        />
      </Form.Item>
    );
  };

  const columns = [
    {
      title: (
        <div className="table-title">
          <BookOutlined /> <span className="name">Cours</span>
        </div>
      ),
      dataIndex: 'subject',
      key: 'column-subject',
      className: 'column-subject',
      sorter: (a, b) => a?.subject?.name.localeCompare(b?.subject?.name),
      render: (text, record) => {
        return {
          children: (
            <Link
              className="link-table"
              to={`/сours/${record._id}`}
              style={{ color: record?.subject?.color }}
            >
              {record?.subject?.name}
            </Link>
          ),
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: 'Niveau',
      dataIndex: 'level',
      key: 'column-level',
      className: 'column-level',
      render: (text, record, index) => {
        return {
          children: coursesLevels(record),
          props: {
            colSpan: 1,
          },
        };
      },
    },
  ];

  // Render
  return (
    <div className={childClassNames} {...rest}>
      <div className="row">
        <AddItemCard
          title="Cours"
          type="section"
          buttonType="button"
          disabeled={disabeled}
          addAction={saveCoursesList}
        />
      </div>
      <div className="row">
        <Form form={coursesSelectedForm} name="courses-list" autoComplete="off">
          <CustomTable
            dataSource={tableRows || []}
            columns={columns}
          />
        </Form>
      </div>
    </div>
  );
}

TabStudentCourses.defaultProps = {
  disabeled: false,
};

TabStudentCourses.propTypes = {
  courses: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
  ]),
  classId: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.object,
    PropTypes.string,
  ]),
  onFormChange: PropTypes.func,
  disabeled: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.string.isRequired,
  ]),
  updateList: PropTypes.func.isRequired,
};

export default memo(TabStudentCourses);
